<template>
<v-dialog
  v-model="value"
  max-width="760px"
  persistent
  scrollable
  content-class="competency-edit-dialog"
>
  <v-card class="rounded-lg" style="border-radius: 12px !important; overflow: hidden;">
    <div v-if="loading" class="loader-overlay">
      <div class="loader-content">
        <v-progress-circular
          indeterminate
          size="48"
          width="4"
          color="#009E60"
        />
        <div class="loader-text mt-3">Сохранение компетенции</div>
      </div>
    </div>
    <v-card-title class="px-6 pt-5 pb-0 d-flex flex-column align-start">
      <div class="d-flex justify-space-between align-start w-100">
        <div>
          <h2 class="text-h6 font-weight-bold mb-1" style="color: #25325F;">
            Библиотека компетенций
          </h2>
          <p class="text-body-2 mb-0" style="color: #009E60; font-weight: 500; max-width: 560px;">
            Редактируйте, удаляйте или добавляйте компетенции — изменения сохраняются локально и применяются при создании диагностики.
          </p>
        </div>
        <v-btn icon small @click="closeModal" class="ml-2 mt-n1">
          <v-icon style="color: #9BA1B5;">mdi-close</v-icon>
        </v-btn>
      </div>
      <v-divider class="mt-4" style="width: 100%;"></v-divider>
    </v-card-title>
    <v-card-text class="px-6 pt-5 pb-2" style="overflow-y: auto; max-height: 60vh;">
      <v-form ref="form" lazy-validation>
      <div class="mb-5">
        <label class="ce-label">Название <span style="color:#EC2E2E;">*</span></label>
        <v-text-field
          v-model="form.name"
          outlined
          dense
          :rules="[v => !!v && v.trim().length > 0 || 'Введите название']"
          placeholder="Введите название"
          class="ce-input mt-1"
        />
      </div>
      <div class="mb-5">
        <label class="ce-label">Описание <span style="color:#EC2E2E;">*</span></label>
        <v-textarea
          v-model="form.description"
          outlined
          dense
          :rules="[v => !!v && v.trim().length > 0 || 'Введите описание']"
          rows="3"
          no-resize
          placeholder="Введите описание"
          class="ce-input mt-1"
        />
      </div>
      <div class="d-flex align-start mb-5" style="gap: 24px; flex-wrap: wrap;">
        <div style="flex: 1; min-width: 200px;">
          <label class="ce-label">Категория <span style="color:#EC2E2E;">*</span></label>
          <v-select
            v-model="form.category"
            :items="categories"
            outlined
            item-text="label"
            dense
            :rules="[v => !!v || 'Выберите категорию']"
            placeholder="Выберите категорию"
            class="ce-input mt-1"
          >
            <template v-slot:item="{ item }">
              {{ item.name }}
            </template>
            <template v-slot:selection="{ item }">
              {{ item.name }}
            </template>
          </v-select>
        </div>
        <div>
          <label class="ce-label">Уровни</label>
          <div class="d-flex mt-1" style="gap: 8px; flex-wrap: wrap;">
            <button
              v-for="level in levels"
              :key="level"
              class="ce-level-btn"
              :class="{ 'ce-level-btn--active': form.levels.includes(level) }"
              type="button"
              @click="toggleLevel(level)"
            >
              {{ level }}
            </button>
          </div>
          <div v-if="levelsError" class="ce-error-text mt-1">Выберите хотя бы один уровень</div>
        </div>
      </div>
      <div>
        <div class="d-flex justify-space-between align-center mb-3">
          <label class="ce-label">Индикаторы</label>
          <v-btn
            text
            small
            height="32"
            style="color: #25325F; font-weight: 600; font-size: 13px; padding: 0 8px;"
            @click="addIndicator"
          >
            <v-icon small class="mr-1">mdi-plus</v-icon>
            Добавить
          </v-btn>
        </div>
        <div
          v-if="!form.indicators.length"
          class="text-body-2 text-center py-4"
          :style="`color: #9BA1B5; border: 1px dashed ${indicatorsError ? '#EC2E2E' : '#DEE1EA'}; border-radius: 8px;`"
        >
          Нет индикаторов. Нажмите «Добавить».
        </div>
        <div v-if="indicatorsError && !form.indicators.length" class="ce-error-text mt-1">Добавьте хотя бы один индикатор</div>

        <!-- Accordion -->
        <v-expansion-panels
          v-else
          v-model="openedPanels"
          multiple
          flat
          class="ce-indicators"
        >
          <v-expansion-panel
            v-for="(indicator, index) in form.indicators"
            :key="index"
            class="ce-indicator-panel mb-2"
          >
            <!-- Заголовок панели -->
            <v-expansion-panel-header class="ce-indicator-header px-4" hide-actions>
              <template #default="{ open }">
                <div class="d-flex align-center justify-space-between w-100">
                  <div class="d-flex align-center" style="gap: 10px; min-width: 0;">
                    <v-icon
                      size="16"
                      style="transition: transform 0.2s; flex-shrink: 0;"
                      :style="open ? 'transform: rotate(90deg); color: #009E60;' : 'color: #9BA1B5;'"
                    >
                      mdi-chevron-right
                    </v-icon>
                    <span
                      class="text-truncate"
                      style="font-size: 14px; font-weight: 600; color: #25325F;"
                    >
                        {{ indicator.name || `Индикатор ${index + 1}` }}
                      </span>
                  </div>
                  <v-btn
                    icon
                    x-small
                    @click.stop="removeIndicator(index)"
                    style="flex-shrink: 0;"
                  >
                    <v-icon size="16" style="color: #EC2E2E;">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="ce-indicator-content">
              <div class="pt-3 pb-1 px-1">
                <div class="mb-4">
                  <label class="ce-label-sm">Название индикатора <span style="color:#EC2E2E;">*</span></label>
                  <v-text-field
                    v-model="indicator.name"
                    outlined
                    dense
                    :rules="[v => !!v && v.trim().length > 0 || 'Введите название индикатора']"
                    placeholder="Введите название индикатора"
                    class="ce-input mt-1"
                  />
                </div>
                <label class="ce-label-sm mb-2 d-block">Описание по уровням</label>
                <div class="ce-levels-grid">
                  <div
                    v-for="lvl in [0, 1, 2, 3]"
                    :key="lvl"
                    class="ce-level-input-wrap"
                  >
                    <div class="ce-level-badge mb-1">
                      <span class="ce-level-badge__num">{{ lvl }}</span>
                      <span class="ce-level-badge__label">{{ levelLabels[lvl] }}</span>
                    </div>
                    <v-textarea
                      v-model="indicator.levels[lvl]"
                      outlined
                      dense
                      hide-details
                      rows="3"
                      no-resize
                      :placeholder="`Уровень ${lvl}`"
                      class="ce-input"
                    />
                  </div>
                </div>

              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
      </v-form>
    </v-card-text>

    <v-divider></v-divider>

    <!-- Footer -->
    <v-card-actions class="px-6 py-4 d-flex justify-space-between">
      <v-btn
        text
        height="40"
        class="px-4"
        style="color: #6B759A; font-weight: 600;"
        @click="closeModal"
      >
        Назад
      </v-btn>
      <v-btn
        depressed
        height="40"
        class="px-8"
        color="#009E60"
        dark
        style="font-weight: 600; border-radius: 8px;"
        @click="save"
      >
        Сохранить
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
</template>

<script>
import { mapMutations } from 'vuex';

export default {
  name: 'CompetencyEditModal',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: Boolean, default: false },
    competency: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      openedPanels: [],
      levelsError: false,
      indicatorsError: false,
      levels: ['junior', 'middle', 'senior', 'top'],
      levelLabels: {
        0: 'Не проявляет', 1: 'Базовый', 2: 'Развитый', 3: 'Эксперт'
      },
      // management, communication, team, leadership, analytics, personal
      categories: [
        {
          name: 'Управление',
          value: 'management',
        },
        {
          name: 'Коммуникации',
          value: 'communication',
        },
        {
          name: 'Команда',
          value: 'team',
        },
        {
          name: 'Лидерство',
          value: 'leadership',
        },
        {
          name: 'Аналитика',
          value: 'analytics',
        },
        {
          name: 'Индивидуальность',
          value: 'personal',
        }
      ],
      form: {
        name: '',
        description: '',
        category: null,
        levels: [],
        indicators: []
      }
    };
  },
  watch: {
    competency: {
      immediate: true,
      handler(val) {
        if (val) {
          this.form = {
            name: val.name || '',
            description: val.description || '',
            category: val.category || null,
            levels: val.level_tags ? [...val.level_tags] : [],
            indicators: val.markers ? val.markers.map(i => ({
              id: i.id || null,
              name: i.name || '',
              levels: {
                0: i.level_0_description || '',
                1: i.level_1_description || '',
                2: i.level_2_description || '',
                3: i.level_3_description || ''
              }
            })) : []
          };
        } else {
          this.resetForm();
        }
      }
    },
    value: {
      immediate: true,
      handler(val) {
        if (!val) {
           this.resetForm();
        }
      }
    },
  },
  methods: {
    ...mapMutations({
      SET_COMPETENCY_SHOW_MODAL: 'diagnostics/diagnotics/SET_COMPETENCY_SHOW_MODAL',
      SET_COMPETENCY_ID_MODAL: 'diagnostics/diagnotics/SET_COMPETENCY_ID_MODAL',
      SET_CURRENT_COMPETENCY: 'diagnostics/diagnotics/SET_CURRENT_COMPETENCY',
    }),
    closeModal() {
      this.SET_COMPETENCY_SHOW_MODAL(false);
      this.SET_COMPETENCY_ID_MODAL(null);
      this.SET_CURRENT_COMPETENCY(null);
      this.resetForm();
    },
    toggleLevel(level) {
      const idx = this.form.levels.indexOf(level);
      if (idx === -1) {
        this.form.levels.push(level);
      } else {
        this.form.levels.splice(idx, 1);
      }
    },
    addIndicator() {
      this.indicatorsError = false;
      this.form.indicators.push({
        name: '',
        levels: {
          0: '',
          1: '',
          2: '',
          3: ''
        }
      });
      // сразу раскрываем новый
      this.$nextTick(() => {
        this.openedPanels.push(this.form.indicators.length - 1);
      });
    },
    removeIndicator(index) {
      this.form.indicators.splice(index, 1);
      this.openedPanels = this.openedPanels
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i);
    },
    save() {
      const formValid = this.$refs.form.validate();
      this.levelsError = this.form.levels.length === 0;
      this.indicatorsError = this.form.indicators.length === 0;
      if (!formValid || this.levelsError || this.indicatorsError) return;
      this.$emit('save', JSON.parse(JSON.stringify(this.form)));
    },
    resetForm() {
      this.form = {
        name: '',
        description: '',
        category: null,
        levels: [],
        indicators: []
      };
      this.openedPanels = [];
      this.levelsError = false;
      this.indicatorsError = false;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    }
  }
};
</script>

<style lang="scss">
.competency-edit-dialog {
  max-height: 90vh;
}

/* ---------- общие ---------- */
.ce-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #25325F;
}

.ce-label-sm {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6B759A;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ce-input {
  .v-input__slot {
    border-radius: 8px !important;
    background-color: #ffffff !important;
    box-shadow: none !important;

    fieldset {
      border-color: #DEE1EA !important;
    }
  }

  &.v-input--is-focused .v-input__slot fieldset {
    border-color: #009E60 !important;
    border-width: 2px !important;
  }

  input, textarea {
    font-size: 14px;
    color: #25325F;

    &::placeholder {
      color: #9BA1B5;
    }
  }
}

/* ---------- toggle уровней ---------- */
.ce-level-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #DEE1EA;
  background: #ffffff;
  font-size: 13px;
  font-weight: 600;
  color: #6B759A;
  cursor: pointer;
  transition: all 0.18s;
  font-family: 'Montserrat', sans-serif;

  &:hover {
    border-color: #009E60;
    color: #009E60;
  }

  &--active {
    background-color: #009E60 !important;
    border-color: #009E60 !important;
    color: #fff !important;
  }
}

/* ---------- индикаторы-аккордион ---------- */
.ce-indicators {
  &.v-expansion-panels {
    background: transparent !important;
  }
}

.ce-indicator-panel {
  border: 1px solid #DEE1EA !important;
  border-radius: 8px !important;
  overflow: hidden;

  &::before {
    box-shadow: none !important;
  }

  &.v-expansion-panel--active {
    border-color: #009E60 !important;
  }
}

.ce-indicator-header {
  min-height: 44px !important;
  background: #F5F8FF !important;

  .v-expansion-panel--active & {
    background: #F0FBF6 !important;
  }
}

.ce-indicator-content {
  .v-expansion-panel-content__wrap {
    padding: 0 16px 12px !important;
  }
}

/* ---------- сетка уровней 0-3 ---------- */
.ce-levels-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.ce-level-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &__num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #25325F;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    color: #6B759A;
  }
}

.ce-error-text {
  font-size: 12px;
  color: #EC2E2E;
}

.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loader-content {
  text-align: center;
}

.loader-text {
  color: #6B759A;
  font-size: 14px;
  font-weight: 500;
}

.blur-content {
  filter: blur(2px);
  pointer-events: none;
  user-select: none;
}
</style>
